import React, { useEffect, useRef } from "react";
import FileUploadButton from "./FileUploadButton";
import FileRender from "./FileRender";
import { Modal } from "bootstrap";
import ReactDOM from 'react-dom';
import { FileManagerFile, StorageProject } from "./FileManagerFile";
import {
    FILE_MANAGER_PAGE_SIZES,
    getPaginationItems
} from "./utils";
import {useSelector} from "react-redux";
import {RootState} from "../../store";
import {UserRole} from "../../types/User";
import { IMeQrStorageData } from "../MeQrStorageService";
import { Trans, useTranslation } from "react-i18next";
import useGetUrlLocale from "../../hooks/useGetUrlLocale";
import Pagination from "./Pagination";
import webViewEventService from "../WebViewEventService";

interface StylesProps {
    files: FileManagerFile[],
    allowedFileTypes: readonly string[],
    onFileUpload: (file: File) => void;
    onFileDelete: (file: FileManagerFile) => void;
    onFileChoose?: (file: FileManagerFile) => void | Promise<void>;
    onModalClose: () => void;
    fileOperationError: string | null;
    isUserPremium: boolean;
    fileUploadLoading: boolean;
    filesLoading: boolean;
    filesError: string | null;
    meQrStorageData: IMeQrStorageData | null;
    meQrStorageLoading: boolean;
    meQrStorageError: "load" | "refresh" | null;
    onRetryMeQrStorage: () => void;
    pageStorageUsed: number;
    uploadProject: StorageProject;
    activeProject: StorageProject;
    projectCounts: Record<StorageProject, number>;
    currentPage: number;
    pageSize: number;
    totalPages: number;
    onProjectChange: (project: StorageProject) => void;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    onRetryFiles: () => void;
}

const FileManager: React.FC<StylesProps> = ({
                                                files,
                                                allowedFileTypes,
                                                onFileUpload,
                                                onFileDelete,
                                                onFileChoose,
                                                onModalClose,
                                                fileOperationError,
                                                isUserPremium,
                                                fileUploadLoading,
                                                filesLoading,
                                                filesError,
                                                meQrStorageData,
                                                meQrStorageLoading,
                                                meQrStorageError,
                                                onRetryMeQrStorage,
                                                pageStorageUsed,
                                                uploadProject,
                                                activeProject,
                                                projectCounts,
                                                currentPage,
                                                pageSize,
                                                totalPages,
                                                onProjectChange,
                                                onPageChange,
                                                onPageSizeChange,
                                                onRetryFiles
                                            }) => {
    const { t } = useTranslation()
    const lng = useGetUrlLocale()
    const locale = lng === "/en" ? "" : lng;
    const meQrAdminAddress = (() => {
        const meQrAddress = process.env.REACT_APP_MEQR_ADDRESS;
        if (!meQrAddress) return "";

        try {
            const url = new URL(meQrAddress);
            if (!url.hostname.startsWith("admin.")) url.hostname = `admin.${url.hostname}`;
            return url.origin;
        } catch {
            return "";
        }
    })();
    const modalRef = useRef<HTMLDivElement | null>(null);
    const modalRootRef = useRef<HTMLDivElement | null>(null);
    let modalContainerHtml: HTMLDivElement = document.getElementById('file-manager-container') as HTMLDivElement;
    if (!modalContainerHtml) {
        modalContainerHtml = document.createElement('div');
        modalContainerHtml.setAttribute('id', 'file-manager-container');
        document.body.appendChild(modalContainerHtml);
    }
    modalRootRef.current = modalContainerHtml;
    const user = useSelector((state: RootState) => state.appStorage.user);
    const isIframeOnMeQr = useSelector(
      (state: RootState) => state.appStorage.isIframeOnMeQr
    );
    const isAdmin = user ? user.roles.includes(UserRole.ROLE_ADMIN) : false;
    const isMeQrWebView = isIframeOnMeQr && webViewEventService.isAvailable();
    const showProjectTabs = projectCounts["me-page"] > 0 && projectCounts["me-qr"] > 0;
    const paginationItems = getPaginationItems(currentPage, totalPages);
    const visibleProject = activeProject;

    useEffect(() => {
        Modal.getOrCreateInstance('#file-manager').show();
        const modalElement = modalRef.current;

        if (modalElement) {
            modalElement.addEventListener('hidden.bs.modal', onModalClose);
        }

        return () => {
            if (modalElement) {
                modalElement.removeEventListener('hidden.bs.modal', onModalClose);
            }
        };
    }, [onModalClose]);

    const privateOnFileChoose = async (file: FileManagerFile) => {
        if(!onFileChoose) return;

        try {
            await onFileChoose(file);
            Modal.getOrCreateInstance('#file-manager').hide();
        } catch {
            // The parent keeps the modal open and renders the operation error.
        }
    };

    const formatFileSize = (size: number) => {
        if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
        return `${(size / (1024 * 1024)).toFixed(2)} MB`;
    };

    const pageStorageLimit = isUserPremium ? 500 * 1024 * 1024 : 100 * 1024 * 1024;
    const pageStoragePercent = pageStorageLimit > 0 ? pageStorageUsed * 100 / pageStorageLimit : 0;
    const isOverLimit = pageStorageUsed > pageStorageLimit;
    const isPageStorageFull = !isAdmin && pageStorageUsed >= pageStorageLimit;
    const isMeQrStorageFull = !isAdmin && !!meQrStorageData
        && meQrStorageData.usedStorageSize >= meQrStorageData.storageSize;
    const isUploadBlockedByStorage = uploadProject === "me-page"
        ? isPageStorageFull
        : meQrStorageLoading || isMeQrStorageFull;
    const isButtonDisabled = fileUploadLoading || filesLoading || isUploadBlockedByStorage;

    return ReactDOM.createPortal(
      <>
        <div
          ref={modalRef}
          className="modal show modal-lg"
          id="file-manager"
          tabIndex={-1}
          role="dialog"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header border-0">
                <h4 className="modal-title text-dark-gray">
                  {t("settings.File Manager")}
                </h4>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  data-bs-dismiss="modal"
                ></button>
              </div>
              <div className="modal-body p-md-4 p-3">
                {showProjectTabs && (
                  <ul
                    className="nav flex-nowrap border-bottom border-1 mb-3 d-flex justify-content-between"
                    role="tablist"
                    aria-label={t("settings.File projects")}
                  >
                    {(["me-page", "me-qr"] as StorageProject[]).map(
                      (project) => (
                        <li key={project} className="nav-item w-50">
                          <button
                            type="button"
                            role="tab"
                            aria-selected={activeProject === project}
                            className={`nav-link rounded-0 fw-bold w-100 fs-5 ${activeProject === project ? "active" : ""}`}
                            onClick={() => onProjectChange(project)}
                          >
                            {project === "me-page" ? "ME-Page" : "ME-QR"}
                            <span className="file-manager-tab-count d-inline-flex align-items-center justify-content-center ms-2 p-1 rounded-pill bg-main-background text-light-blue fs-7 fw-normal">
                              {projectCounts[project]}
                            </span>
                          </button>
                        </li>
                      )
                    )}
                  </ul>
                )}
                <div className="border p-4 rounded-top">
                  <div className="row">
                    <div className="col-12 col-md-4 mb-3 mb-md-0">
                      <FileUploadButton
                        allowedFileTypes={allowedFileTypes}
                        onSuccess={onFileUpload}
                        fileUploadLoading={fileUploadLoading}
                        disabled={isButtonDisabled}
                      />
                    </div>
                    <div className="col-12 col-md-8">
                      <div className="bg-secondary-light px-3 h-100 d-flex align-items-center rounded py-2">
                        <p className="mb-0 text-light-blue fs-7">
                          <Trans
                            i18nKey="settings.fileInfo"
                            components={[<b />]}
                          />
                        </p>
                      </div>
                    </div>
                  </div>
                  {fileOperationError && (
                    <div className="text-danger mt-2">{fileOperationError}</div>
                  )}
                </div>

                <div className="p-4 border border-top-0">
                  <div className="row gy-3 overflow-y-auto file-render-container">
                    {filesLoading ? (
                      <div className="col-12 d-flex align-items-center justify-content-center file-manager-empty">
                        <div
                          className="spinner-border text-primary"
                          role="status"
                        >
                          <span className="visually-hidden">
                            {t("settings.Loading")}...
                          </span>
                        </div>
                      </div>
                    ) : filesError ? (
                      <div
                        className="col-12 d-flex flex-column align-items-center justify-content-center gap-2 file-manager-empty text-danger"
                        role="alert"
                      >
                        <span>{filesError}</span>
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0"
                          onClick={onRetryFiles}
                        >
                          {t("settings.Retry")}
                        </button>
                      </div>
                    ) : (
                      <FileRender
                        onFileDelete={onFileDelete}
                        onFileChoose={privateOnFileChoose}
                        files={files}
                      />
                    )}
                  </div>
                  {projectCounts[visibleProject] >
                    FILE_MANAGER_PAGE_SIZES[0] && (
                    <Pagination
                      currentPage={currentPage}
                      pageSize={pageSize}
                      pageSizes={FILE_MANAGER_PAGE_SIZES}
                      paginationItems={paginationItems}
                      totalPages={totalPages}
                      onPageChange={onPageChange}
                      onPageSizeChange={onPageSizeChange}
                    />
                  )}
                </div>
                <div className="p-3 border border-top-0">
                  <div className="border border-warning rounded d-flex align-items-center bg-warning-subtle mb-3 px-2 py-1">
                    <i className="bi bi-exclamation-circle text-warning me-2"></i>
                    <p className="text-warning mb-0 fs-7">
                      {t("settings.fileDelitingInfo")}
                    </p>
                  </div>
                  {visibleProject === "me-page" ? (
                    <>
                      {!isAdmin && (
                        <div
                          className="bg-main-background position-relative overflow-hidden rounded-4"
                          style={{ height: "8px" }}
                        >
                          <div
                            className={`h-100 ${isOverLimit ? "bg-danger" : "bg-blue"} position-absolute translate-middle-y top-50 w-100 rounded-4`}
                            style={{
                              right:
                                pageStoragePercent > 100
                                  ? "0"
                                  : `calc(100% - ${Math.max(0, pageStoragePercent)}%)`
                            }}
                          ></div>
                        </div>
                      )}
                      <div className="d-flex align-items-center justify-content-between mb-0 mt-2">
                        <p className="text-background-mobile mb-0 fs-7">
                          {isAdmin
                            ? t("upload.usedStorageOnly", {
                                used: formatFileSize(pageStorageUsed)
                              })
                            : t("upload.usedStorage", {
                                used: formatFileSize(pageStorageUsed),
                                limitText: formatFileSize(pageStorageLimit)
                              })}
                        </p>
                        {!isUserPremium && (
                          <p className="text-background-mobile fs-7 mb-0">
                            {t("settings.Need more space? Upgrade to")}
                            <a
                              className="ms-2 text-blue"
                              href={`${process.env.REACT_APP_BACKEND_ADDRESS}${lng}/pricing/#pricingSection`}
                            >
                              {t("settings.Premium")}
                            </a>
                          </p>
                        )}
                      </div>
                    </>
                  ) : meQrStorageLoading ? (
                    <div className="d-flex align-items-center gap-2 text-background-mobile fs-7">
                      <div
                        className="spinner-border spinner-border-sm"
                        role="status"
                      >
                        <span className="visually-hidden">
                          {t("settings.Loading")}...
                        </span>
                      </div>
                      {t("settings.Loading")}...
                    </div>
                  ) : meQrStorageError ? (
                    <div
                      className="d-flex align-items-center justify-content-between gap-3 text-danger fs-7"
                      role="alert"
                    >
                      <span>
                        {t(
                          meQrStorageError === "refresh"
                            ? "settings.storageRefreshFailed"
                            : "settings.storageLoadFailed"
                        )}
                      </span>
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0"
                        onClick={onRetryMeQrStorage}
                      >
                        {t("settings.Retry")}
                      </button>
                    </div>
                  ) : (
                    meQrStorageData && (
                      <>
                        <div
                          className="bg-main-background position-relative overflow-hidden rounded-4"
                          style={{ height: "8px" }}
                        >
                          <div
                            className={`h-100 ${(meQrStorageData.usedStorageSize * 100) / meQrStorageData.storageSize > 90 ? "bg-danger" : "bg-blue"} position-absolute translate-middle-y top-50 w-100 rounded-4`}
                            style={{
                              right:
                                meQrStorageData.usedStorageSize >
                                meQrStorageData.storageSize
                                  ? "0"
                                  : `calc(100% - ${(meQrStorageData.usedStorageSize * 100) / meQrStorageData.storageSize}%)`
                            }}
                          ></div>
                        </div>
                        <div className="d-flex align-items-center justify-content-between mt-2">
                          <p className="text-background-mobile mb-0 fs-7">
                            {t("upload.usedStorage", {
                              used: formatFileSize(
                                meQrStorageData.usedStorageSize
                              ),
                              limitText: formatFileSize(
                                meQrStorageData.storageSize
                              )
                            })}
                          </p>
                          <p className="text-background-mobile fs-7 mb-0">
                            {t("settings.Need more storage size?")}
                            {isMeQrWebView ? (
                              <button
                                type="button"
                                className="btn btn-link ms-2 p-0 text-blue fs-7 align-baseline"
                                onClick={() =>
                                  webViewEventService.manageStorage()
                                }
                              >
                                {t("settings.Buy Extra Storage")}
                              </button>
                            ) : (
                              <a
                                className="ms-2 text-blue"
                                href={`${meQrAdminAddress}${locale}/subscription`}
                                target="_top"
                              >
                                {t("settings.Buy Extra Storage")}
                              </a>
                            )}
                          </p>
                        </div>
                      </>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </>,
      modalRootRef.current
    );
};

export default FileManager;
